import React, { useEffect, useState } from "react";
import { api, formatApiError, formatRupiah } from "@/lib/api";
import { PageHeader, Section, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Check, X } from "lucide-react";
import HistoryButton from "@/components/HistoryButton";
import ExportImport from "@/components/ExportImport";

export default function Purchasing() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "PR", status: "pending", items: [] });
  const [lineItem, setLineItem] = useState({ name: "", qty: 1, price: 0 });

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/purchasing"), api.get("/sites")]);
    setItems(p.data); setSites(s.data);
  };
  useEffect(() => { load(); }, []);

  const addLine = () => {
    if (!lineItem.name) return;
    setForm({ ...form, items: [...(form.items || []), { ...lineItem, qty: Number(lineItem.qty), price: Number(lineItem.price) }] });
    setLineItem({ name: "", qty: 1, price: 0 });
  };
  const removeLine = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const total = (form.items || []).reduce((s, i) => s + i.qty * i.price, 0);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/purchasing", { ...form, total });
      toast.success("Purchase Request dibuat");
      setForm({ type: "PR", status: "pending", items: [] });
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const approve = async (id, status) => {
    await api.post(`/purchasing/${id}/approve`, { status });
    toast.success("Status diperbarui");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Purchasing (PR / PO)"
        subtitle="Purchase Request & Purchase Order dengan approval"
        testId="purchasing-header"
        actions={
          <>
          <ExportImport module="purchasing" canImport={false} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="purchasing-add-btn" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold"><Plus size={16} className="mr-1" /> Buat PR/PO</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-3xl">
              <DialogHeader><DialogTitle className="font-display font-black text-2xl">Buat Purchase Request/Order</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-400">Tipe</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                        <SelectItem value="PR">PR</SelectItem>
                        <SelectItem value="PO">PO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-400">Tanggal</Label>
                    <Input required type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-400">Supplier</Label>
                    <Input value={form.supplier || ""} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                  </div>
                </div>

                <div className="border border-neutral-800 p-3">
                  <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">// LINE ITEMS</div>
                  <div className="grid grid-cols-12 gap-2">
                    <Input placeholder="Nama Item" className="col-span-5 rounded-none bg-neutral-900 border-neutral-800 h-10" value={lineItem.name} onChange={(e) => setLineItem({ ...lineItem, name: e.target.value })} />
                    <Input placeholder="Qty" type="number" className="col-span-2 rounded-none bg-neutral-900 border-neutral-800 h-10" value={lineItem.qty} onChange={(e) => setLineItem({ ...lineItem, qty: e.target.value })} />
                    <Input placeholder="Harga" type="number" className="col-span-3 rounded-none bg-neutral-900 border-neutral-800 h-10" value={lineItem.price} onChange={(e) => setLineItem({ ...lineItem, price: e.target.value })} />
                    <Button type="button" onClick={addLine} className="col-span-2 rounded-none bg-neutral-800 hover:bg-neutral-700">+ Item</Button>
                  </div>
                  {form.items?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {form.items.map((it, i) => (
                        <div key={i} className="flex items-center justify-between text-sm font-mono bg-neutral-900 px-3 py-2">
                          <span>{it.name} × {it.qty}</span>
                          <span className="flex items-center gap-3">
                            {formatRupiah(it.qty * it.price)}
                            <button type="button" onClick={() => removeLine(i)}><Trash2 size={12} className="text-[#DC2626]" /></button>
                          </span>
                        </div>
                      ))}
                      <div className="text-right font-display font-black text-xl mt-2">TOTAL: {formatRupiah(total)}</div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-neutral-800">Batal</Button>
                  <Button type="submit" data-testid="purchasing-submit-btn" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">Simpan</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </>
        }
      />
      <Section>
        {items.length === 0 ? <Empty /> : (
          <div className="border border-neutral-800 bg-[#141414]">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tanggal</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tipe</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Supplier</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Items</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Total</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="border-neutral-800">
                    <TableCell className="font-mono text-xs">{p.date}</TableCell>
                    <TableCell><span className="text-xs font-mono uppercase px-2 py-1 border border-[#FF6600] text-[#FF6600]">{p.type}</span></TableCell>
                    <TableCell className="font-mono text-xs">{p.supplier || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{p.items?.length || 0} baris</TableCell>
                    <TableCell className="font-mono font-bold">{formatRupiah(p.total)}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-mono uppercase px-2 py-1 border ${
                        p.status === "approved" ? "border-[#16A34A] text-[#16A34A]" :
                        p.status === "rejected" ? "border-[#DC2626] text-[#DC2626]" :
                        "border-[#FFD700] text-[#FFD700]"
                      }`}>{p.status}</span>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <HistoryButton entityType="purchasing" entityId={p.id} />
                      {(p.status === "draft" || p.status === "pending") && (
                        <>
                          <button onClick={() => approve(p.id, "approved")} data-testid={`po-approve-${p.id}`} className="text-[#16A34A]"><Check size={16} /></button>
                          <button onClick={() => approve(p.id, "rejected")} data-testid={`po-reject-${p.id}`} className="text-[#DC2626]"><X size={16} /></button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}
